import React, {
  createContext,
  useState,
  useEffect,
  ReactNode,
  useContext,
  useRef,
} from "react";
import {
  Notification,
  SendNotificationRequest,
  BroadcastNotificationRequest,
  NotificationsListResponse,
  ApiDataResponse,
} from "../services/Dtos";
import { useUserContext } from "./userContext";
import useAxios from "../hooks/useAxios";
import { useGlobalError } from "./GlobalErrorContext";
import { notificationService } from "../services/notificationService";
import { IconType } from "react-icons";
import useIcons from "../hooks/useIcons";
import { NotificationType } from "../services/Enums";

interface NotificationContextType {
  notifications: Notification[];
  setNotifications: React.Dispatch<React.SetStateAction<Notification[]>>;
  unreadCount: number;
  isLoading: boolean;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  error: string | null;
  connectionState: string;
  fetchNotifications: () => Promise<void>;
  markAsRead: (notificationId: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  getNotificationTypeLabel: (type: NotificationType) => {
    label: string;
    color: string;
    icon: IconType;
  };
  sendNotification: (
    request: SendNotificationRequest
  ) => Promise<ApiDataResponse<Notification> | null>;
  deleteNotification: (notificationId: string) => Promise<boolean>;
  clearAllNotifications: () => Promise<void>;
}

interface Props {
  children: ReactNode;
}

const NotificationContext = createContext<NotificationContextType | undefined>(
  undefined
);

export const NotificationProvider = ({ children }: Props) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [connectionState, setConnectionState] =
    useState<string>("Disconnected");
  const { user, isLoading: userLoading } = useUserContext();
  const axiosClient = useAxios();
  const { setError: setGlobalError } = useGlobalError();
  const fetchNotificationsRef = useRef<() => Promise<void>>();
  const isInitialized = useRef(false);
  const pollingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(
    null
  );
  const icons = useIcons();
  // Calculate unread count
  const unreadCount = notifications.filter((n) => !n.isRead).length;

  const fetchNotifications = async () => {
    if (!user) return;

    setIsLoading(true);
    try {
      setError(null);
      const response =
        await axiosClient.get<NotificationsListResponse>("notifications");

      if (response.data?.succeeded) {
        setNotifications(response.data.data);
      } else {
        const errorMessage = `Error fetching notifications: ${response.data?.message}`;
        setError(errorMessage);
        setGlobalError({
          message: errorMessage,
          onRetry: () => {
            // Use a timeout to avoid immediate recursion
            setTimeout(() => {
              if (fetchNotificationsRef.current) {
                fetchNotificationsRef.current();
              }
            }, 1000);
          },
        });
      }
    } catch (error) {
      const errorMessage = "Failed to fetch notifications";
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // Store the latest version of fetchNotifications in ref
  fetchNotificationsRef.current = fetchNotifications;

  const markAsRead = async (notificationId: string) => {
    try {
      const response = await axiosClient.put<ApiDataResponse<boolean>>(
        `notifications/${notificationId}/mark-read`
      );

      if (response.data?.succeeded) {
        setNotifications((prev) =>
          prev.map((n) =>
            n.id === notificationId ? { ...n, isRead: true } : n
          )
        );
      }
    } catch (error) {}
  };

  const markAllAsRead = async () => {
    try {
      const response = await axiosClient.put<ApiDataResponse<boolean>>(
        "notifications/mark-all-read"
      );

      if (response.data?.succeeded) {
        setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
      }
    } catch (error) {}
  };

  const sendNotification = async (
    request: SendNotificationRequest
  ): Promise<ApiDataResponse<Notification> | null> => {
    try {
      let endpoint = "notifications/send";
      let payload: SendNotificationRequest | BroadcastNotificationRequest =
        request;

      // Use different endpoint and payload for broadcast messages
      if (request.type === NotificationType.BroadcastMessage) {
        endpoint = "notifications/broadcast";
        payload = {
          title: request.title,
          message: request.message,
        };
      }

      const response = await axiosClient.post<ApiDataResponse<Notification>>(
        endpoint,
        payload
      );

      if (response.data?.succeeded) {
        return response.data;
      }
      return null;
    } catch (error) {
      return null;
    }
  };

  const deleteNotification = async (
    notificationId: string
  ): Promise<boolean> => {
    try {
      const response = await axiosClient.delete<ApiDataResponse<boolean>>(
        `notifications/${notificationId}`
      );

      if (response.data?.succeeded) {
        setNotifications((prev) => prev.filter((n) => n.id !== notificationId));
        return true;
      }
      return false;
    } catch (error) {
      return false;
    }
  };

  const clearAllNotifications = async () => {
    try {
      const response = await axiosClient.delete<ApiDataResponse<boolean>>(
        "notifications/delete-all"
      );

      if (response.data?.succeeded) {
        setNotifications([]);
      }
    } catch (error) {}
  };

  const getNotificationTypeLabel = (
    type: NotificationType
  ): { label: string; color: string; icon: IconType } => {
    switch (type) {
      case NotificationType.DirectMessage:
        return {
          label: "Direct Message",
          color: "blue",
          icon: icons.utility.chat,
        };
      case NotificationType.BroadcastMessage:
        return {
          label: "Broadcast",
          color: "cyan",
          icon: icons.utility.broadcast,
        };
      case NotificationType.SystemMessage:
        return {
          label: "System Message",
          color: "teal",
          icon: icons.utility.settings,
        };
      case NotificationType.DashboardUpdate:
        return {
          label: "Dashboard Update",
          color: "orange",
          icon: icons.main.dashboard,
        };
      case NotificationType.ResultUpdate:
        return {
          label: "Result Update",
          color: "green",
          icon: icons.main.result,
        };
      default:
        return {
          label: "Unknown",
          color: "gray",
          icon: icons.utility.notification,
        };
    }
  };

  // SignalR connection and real-time updates setup
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!user || !token || userLoading || isInitialized.current) {
      setIsLoading(false);
      return;
    }

    const initializeSignalR = async () => {
      try {
        // Connect to SignalR
        notificationService.connect({
          userId: user.id,
          schoolId: user.schoolId,
        });

        setConnectionState("Connected");

        // Set up SignalR event handlers for direct messages
        notificationService.onNotificationReceived((notification) => {
          setNotifications((prev) => [notification, ...prev]);
        });

        // Set up SignalR event handlers for broadcast messages
        notificationService.onBroadcastReceived((notification) => {
          setNotifications((prev) => [notification, ...prev]);
        });

        // Initial fetch of notifications
        await fetchNotifications();
      } catch (error) {
        setConnectionState("Failed");
        setError("Failed to initialize real-time notification service");
      } finally {
        setIsLoading(false);
        isInitialized.current = true;
      }
    };

    initializeSignalR();

    // Cleanup function
    return () => {
      notificationService.disconnect();
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      setConnectionState("Disconnected");
    };
  }, [user?.id, userLoading]); // Only depend on user ID, not the entire user object

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        setNotifications,
        unreadCount,
        isLoading,
        setIsLoading,
        error,
        connectionState,
        fetchNotifications,
        markAsRead,
        markAllAsRead,
        sendNotification,
        deleteNotification,
        clearAllNotifications,
        getNotificationTypeLabel,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotificationContext = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error(
      "useNotificationContext must be used within a NotificationProvider"
    );
  }
  return context;
};
